import { Input, Output, Component, EventEmitter, OnInit } from '@angular/core'
import { IConfigScreenComponent } from '../../../angular/components/exam-screen/'
import { Screen, TestExecution } from '../../../data/models'
import { TestExecutionMeta } from '../../../data/models/test-execution-meta/'
import { FormBuilder, FormGroup, FormControl } from '@angular/forms'
import { TestExecutionMetaService } from '../../../data/services/test-execution-meta'
import { Utils } from '../../../helpers/utils'
import { ObservableUtils } from '../../../helpers/observable-utils'
import { GuaranteedFindDecorator, GuaranteedFindDecoratorFactory } from '../../../../lib/model-repository/guaranteed-find/'

const EXAM_TIMER_CONFIG_KEY = 'TimerConfig'
const TEST_CONFIG_KEY = 'exam_configurations'

@Component({
  selector: 'mp-exam-configuration-screen',
  templateUrl: './exam-configuration-screen.html',
  styleUrls: ['./exam-configuration-screen.scss']
})
export class ExamConfigurationScreenComponent implements IConfigScreenComponent, OnInit {
  @Input() public screen: Screen
  @Input() public execution: TestExecution

  @Output() public onUiChangesRequested: EventEmitter<any> = new EventEmitter<any>()

  private _testExecutionMetaService: GuaranteedFindDecorator<TestExecutionMeta>
  private _testExecutionMeta: TestExecutionMeta

  public get examConfigs() {
    return this.screen.getConfig(TEST_CONFIG_KEY)
  }

  public examConfigForm: FormGroup

  constructor(
    private _formBuilder: FormBuilder,
    private _utils: Utils,
    private _observableUtils: ObservableUtils,
    _guaranteedFindDecoratorFactory: GuaranteedFindDecoratorFactory,
  ) {
    this._testExecutionMetaService = _guaranteedFindDecoratorFactory.get(TestExecutionMetaService)
  }

  public ngOnInit() {
    this._testExecutionMetaService.findModelsBy(
        {testExecution: this.execution, key: EXAM_TIMER_CONFIG_KEY},
        ['testExecution'],
        this._getDefaultArgs(this.execution)
      ).subscribe((testExecutionMeta) => {
        this._testExecutionMeta = testExecutionMeta[0] as TestExecutionMeta
        this.examConfigForm = this._formBuilder.group(
          this._setupExamConfigForm()
        )

        this._setupNextAction()
      })
  }

  public formatExamConfigType(name) {
    return this._utils.capitalizeFirst(name)
  }

  private _getDefaultArgs(execution: TestExecution) {
    return {
      key: EXAM_TIMER_CONFIG_KEY,
      testExecution: execution,
      value: JSON.stringify({})
    }
  }

  private _setupNextAction() {
    this.onUiChangesRequested.emit({
      buttons: [{
        type: 'next',
        disabled: this.examConfigForm.invalid,
        action: () => this._updateExamConfigs()
      }]
    })
  }

  private _updateExamConfigs() {
    return this._observableUtils.lazyObservable(() => {
      for (const configType in this.examConfigForm.value) {
        const functionName = `_update${this.formatExamConfigType(configType)}Config`
        const actionFn = this[functionName] || (() => { throw new Error(`Function '${functionName}' does not exist!`) })
        const options = this.examConfigs.find((configData) => configData.type === configType)
        actionFn.call(this, this.examConfigForm.value[configType], options)
      }
    }).concatMap(() => this._testExecutionMetaService.save(this._testExecutionMeta))
  }

  private _updateTimingConfig(timingMultiplier, options) {
    const sectionTimes = options['default_section_times']

    for (const sectionType in sectionTimes) {
      sectionTimes[sectionType] *= timingMultiplier
    }
    const requiresAccommodation = timingMultiplier != options['default']
    this._testExecutionMeta.setJsonValue({
      section_times: sectionTimes,
      requires_accommodation: requiresAccommodation,
      custom_time_resolver: options['custom_time_resolver']
    })
  }

  private _updateDefaultTimingConfig(timingMultiplier, options) {
    const allotedTime = options['allottedTime'] * timingMultiplier
    this._testExecutionMeta.setJsonValue({
      allotedTime: allotedTime
    })
  }

  private _updateMetadataTimingConfig(timingMultiplier, options) {
    const allotedTime = this._testExecutionMeta.getJsonValue()['dynamic_allotted_time']
    if (!allotedTime) {
      throw new Error('Allotted time most be set in the metadata')
    }
    this._updateDefaultTimingConfig(timingMultiplier, {allottedTime: allotedTime})
  }

  private _setupExamConfigForm() {
    const result = {}
    this.examConfigs.forEach((examConfig) => {
      result[examConfig.type] = new FormControl(null)
      result[examConfig.type].setValue(examConfig.default || '')
    })
    return result
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/angular/components/exam-configuration-screen/exam-configuration-screen.component.ts